import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { fetchHotels as fetchHotelsApi, getHotel } from "@/lib/hotelsApi";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { NumericInput } from "@/components/ui/numeric-input";
import { MoneyInput } from "@/components/ui/money-input";
import { DateInput } from "@/components/ui/date-input";
import { PriceWithAdjustment } from "@/components/proposals/PriceWithAdjustment";

interface AccommodationStepProps {
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

const MEAL_OPTION_PRICE_KEY: Record<string, string> = {
  BB: "price_breakfast",
  HB: "price_half_board",
  FB: "price_full_board",
  AI: "price_room_only",
};

const getAccommodationNights = (checkIn?: string | null, checkOut?: string | null): number => {
  if (!checkIn || !checkOut) return 1;
  const inDate = new Date(checkIn);
  const outDate = new Date(checkOut);
  if (Number.isNaN(inDate.getTime()) || Number.isNaN(outDate.getTime())) return 1;
  const nights = Math.ceil((outDate.getTime() - inDate.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(1, nights);
};

const computeAccommodationBase = (acc: any): number => {
  if (!acc || acc.to_be_quoted || acc.free_of_charge || !acc.booking_with_us) return 0;
  const rooms = Math.max(1, Number(acc.number_of_rooms || 1));
  const nights = getAccommodationNights(acc.check_in, acc.check_out);
  const hcteNightRate = Number(acc.hcte_night_rate ?? acc.booking_price ?? 0);
  if (Number.isFinite(hcteNightRate) && hcteNightRate > 0) {
    return hcteNightRate * rooms * nights;
  }
  if (acc.hotel_rooms) {
    const mealKey = MEAL_OPTION_PRICE_KEY[String(acc.meal_option || "").toUpperCase()] || "price_breakfast";
    const roomPrice = Number(acc.hotel_rooms[mealKey] || 0);
    if (Number.isFinite(roomPrice) && roomPrice > 0) {
      return roomPrice * rooms * nights;
    }
  }
  return 0;
};

const AccommodationStep = ({ data, onChange, readOnly }: AccommodationStepProps) => {
  const isReadOnly = !!readOnly;
  const [hotels, setHotels] = useState<any[]>([]);
  const [rooms, setRooms] = useState<Record<number, any[]>>({});
  const [accommodations, setAccommodations] = useState<any[]>([]);
  const lastInternalSyncSignatureRef = useRef<string>("");

  const initialAccommodation = {
    hotel_id: null as number | null,
    room_id: null as number | null,
    meal_option: "BB",
    number_of_rooms: 1,
    check_in: "",
    check_out: "",
    booking_with_us: false,
    booking_price: null as number | null,
    hcte_night_rate: null as number | null,
    accommodation_website_night_rate: null as number | null,
    booking_com_night_rate: null as number | null,
    free_of_charge: false,
    to_be_quoted: true,
    cancellation_policy: "",
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  useEffect(() => {
    const incomingAccommodations = Array.isArray(data.accommodations) ? data.accommodations : [];
    const incomingSignature = buildSyncSignature(incomingAccommodations);

    // Ignore state echo from parent after local edits to preserve input focus.
    if (incomingSignature === lastInternalSyncSignatureRef.current) {
      return;
    }

    if (incomingAccommodations.length === 0) {
      setAccommodations([]);
      return;
    }

    const sorted = [...incomingAccommodations].sort((a, b) => {
      if (!a.check_in) return -1;
      if (!b.check_in) return 1;
      return a.check_in.localeCompare(b.check_in);
    });

    setAccommodations(sorted);

    sorted.forEach((acc) => {
      if (acc.hotel_id) {
        fetchRooms(acc.hotel_id);
      }
    });
  }, [data.accommodations]);

  const fetchHotels = async () => {
    const payload = await fetchHotelsApi({ page: 1, size: 1000, sortField: "hotel_name", sortOrder: "asc" });
    const items = payload.items || [];
    setHotels(items);
  };

  const fetchRooms = async (hotelId: number) => {
    try {
      const hotel = await getHotel(hotelId);
      setRooms((prev) => ({ ...prev, [hotelId]: hotel?.rooms || [] }));
      return hotel;
    } catch {
      setRooms((prev) => ({ ...prev, [hotelId]: [] }));
      return null;
    }
  };

  const buildSyncSignature = (items: any[] | undefined | null) =>
    JSON.stringify(
      (items || []).map((item: any) => ({
        id: typeof item?.id === "number" ? item.id : null,
        hotel_id: item?.hotel_id ?? null,
        room_id: item?.room_id ?? null,
        meal_option: item?.meal_option ?? "",
        number_of_rooms: Number(item?.number_of_rooms ?? 1),
        check_in: (item?.check_in || "").toString(),
        check_out: (item?.check_out || "").toString(),
        booking_with_us: !!item?.booking_with_us,
        booking_price: item?.booking_price ?? null,
        hcte_night_rate: item?.hcte_night_rate ?? item?.booking_price ?? null,
        accommodation_website_night_rate: item?.accommodation_website_night_rate ?? null,
        booking_com_night_rate: item?.booking_com_night_rate ?? null,
        free_of_charge: !!item?.free_of_charge,
        to_be_quoted: !!item?.to_be_quoted,
        cancellation_policy: (item?.cancellation_policy || "").toString(),
      }))
    );

  const emitAccommodationsChange = (nextAccommodations: any[]) => {
    lastInternalSyncSignatureRef.current = buildSyncSignature(nextAccommodations);
    onChange({ ...data, accommodations: nextAccommodations });
  };

  const addAccommodation = () => {
    if (isReadOnly) return;
    const newAccommodations = [
      { ...initialAccommodation },
      ...accommodations,
    ];
    setAccommodations(newAccommodations);
    emitAccommodationsChange(newAccommodations);
  };

  const removeAccommodation = (index: number) => {
    if (isReadOnly) return;
    const newAccommodations = accommodations.filter((_, i) => i !== index);
    setAccommodations(newAccommodations);
    emitAccommodationsChange(newAccommodations);
  };

  const updateAccommodation = (index: number, field: string, value: any) => {
    if (isReadOnly) return;
    const newAccommodations = [...accommodations];
    newAccommodations[index] = { ...newAccommodations[index], [field]: value };
    
    if (field === "hotel_id") {
      newAccommodations[index].room_id = null;
      newAccommodations[index].hotels = undefined;
      newAccommodations[index].hotel_rooms = undefined;
      setAccommodations(newAccommodations);
      emitAccommodationsChange(newAccommodations);

      if (value) {
        fetchRooms(value).then((selectedHotel) => {
          if (!selectedHotel) return;
          const updated = [...newAccommodations];
          updated[index] = {
            ...updated[index],
            hotels: {
              id: selectedHotel.id,
              hotel_name: selectedHotel.hotel_name,
              location: selectedHotel.location,
              address: selectedHotel.address,
              stars: selectedHotel.stars,
              description: selectedHotel.description,
              amenities: selectedHotel.amenities,
              image_url: selectedHotel.image_url,
              cancellation_policy: selectedHotel.cancellation_policy,
            },
          };
          setAccommodations(updated);
          emitAccommodationsChange(updated);
        });
      }
      return;
    }

    if (field === "room_id") {
      const hotelRooms = rooms[newAccommodations[index].hotel_id] || [];
      const selectedRoom = hotelRooms.find((r) => r.id === value);
      if (selectedRoom) {
        newAccommodations[index].hotel_rooms = {
          id: selectedRoom.id,
          room_name: selectedRoom.room_name,
          room_description: selectedRoom.room_description,
          room_image_url: selectedRoom.room_image_url,
          price_room_only: selectedRoom.price_room_only,
          price_breakfast: selectedRoom.price_breakfast,
          price_half_board: selectedRoom.price_half_board,
          price_full_board: selectedRoom.price_full_board,
          max_occupancy: selectedRoom.max_occupancy,
        };
      } else {
        newAccommodations[index].hotel_rooms = undefined;
      }
    }

    if (field === "hcte_night_rate") {
      // Keep legacy field synchronized for existing calculations/data.
      newAccommodations[index].booking_price = value;
    }
    
    if (field === "free_of_charge" && value === true) {
      newAccommodations[index].booking_with_us = false;
      newAccommodations[index].to_be_quoted = false;
    }

    if (field === "booking_with_us" && value === true) {
      newAccommodations[index].to_be_quoted = false;
      newAccommodations[index].free_of_charge = false;
    }

    if (field === "to_be_quoted" && value === true) {
      newAccommodations[index].booking_with_us = false;
      newAccommodations[index].free_of_charge = false;
    }
    
    setAccommodations(newAccommodations);
    emitAccommodationsChange(newAccommodations);
  };

  const parseMoneyValue = (value: string): number | null => {
    if (value.trim() === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Accommodation</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add hotel accommodations for the trip
          </p>
        </div>
        <Button type="button" size="sm" onClick={addAccommodation} disabled={isReadOnly}>
          <Plus className="mr-2 h-4 w-4" />
          Add Accommodation
        </Button>
      </div>

      {accommodations.length === 0 ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
          onClick={isReadOnly ? undefined : addAccommodation}
          onKeyDown={isReadOnly ? undefined : (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addAccommodation();
            }
          }}
          role={isReadOnly ? undefined : "button"}
          tabIndex={isReadOnly ? -1 : 0}
          aria-label={isReadOnly ? undefined : "Add accommodation"}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No accommodations added yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Accommodation" to get started
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {accommodations.map((accommodation, index) => (
            <div key={index} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-3 right-3 h-8 w-8 p-0"
                onClick={() => removeAccommodation(index)}
                disabled={isReadOnly}
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Hotel</Label>
                  <SearchableSelect
                    items={hotels.map((h) => ({
                      id: h.id,
                      title: h.hotel_name,
                      description: h.address || h.location || undefined,
                    }))}
                    value={accommodation.hotel_id ?? undefined}
                    onValueChange={(value) => updateAccommodation(index, "hotel_id", parseInt(value.toString()))}
                    placeholder="Select hotel"
                    emptyText="No hotel found."
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Room Type</Label>
                  <Select
                    value={accommodation.room_id ? accommodation.room_id.toString() : undefined}
                    onValueChange={(value) => updateAccommodation(index, "room_id", parseInt(value))}
                    disabled={isReadOnly || !accommodation.hotel_id}
                  >
                    <SelectTrigger disabled={isReadOnly || !accommodation.hotel_id}>
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms[accommodation.hotel_id]?.map((r) => (
                        <SelectItem key={r.id} value={r.id.toString()}>
                          {r.room_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Meal Option</Label>
                    <Select
                      value={accommodation.meal_option || "BB"}
                      onValueChange={(value) => updateAccommodation(index, "meal_option", value)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger disabled={isReadOnly}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BB">Bed & Breakfast</SelectItem>
                        <SelectItem value="HB">Half Board</SelectItem>
                        <SelectItem value="FB">Full Board</SelectItem>
                        <SelectItem value="AI">All Inclusive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Number of Rooms</Label>
                    <NumericInput
                      value={accommodation.number_of_rooms ?? 1}
                      onChange={(value) => {
                        const val = parseInt(value) || 0;
                        if (val >= 0) {
                          updateAccommodation(index, "number_of_rooms", val);
                        }
                      }}
                      allowDecimals={false}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Check-in</Label>
                    <DateInput
                      value={accommodation.check_in || ""}
                      onChange={(value) => updateAccommodation(index, "check_in", value)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Check-out</Label>
                    <DateInput
                      value={accommodation.check_out || ""}
                      onChange={(value) => updateAccommodation(index, "check_out", value)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
                  <div className="flex min-h-10 items-center space-x-2">
                    <Checkbox
                      checked={!!accommodation.to_be_quoted}
                      onCheckedChange={(checked) => updateAccommodation(index, "to_be_quoted", Boolean(checked))}
                      disabled={isReadOnly}
                    />
                    <Label className="text-sm font-normal cursor-pointer">To Be Quoted</Label>
                  </div>
                  <div className="flex min-h-10 items-center space-x-2">
                    <Checkbox
                      checked={!!accommodation.booking_with_us}
                      onCheckedChange={(checked) => updateAccommodation(index, "booking_with_us", Boolean(checked))}
                      disabled={isReadOnly}
                    />
                    <Label className="text-sm font-normal cursor-pointer">Booking With Us</Label>
                  </div>
                  <div className="flex min-h-10 items-center space-x-2">
                    <Checkbox
                      checked={!!accommodation.free_of_charge}
                      onCheckedChange={(checked) => updateAccommodation(index, "free_of_charge", Boolean(checked))}
                      disabled={isReadOnly}
                    />
                    <Label className="text-sm font-normal cursor-pointer">Free of Charge (FOC)</Label>
                  </div>
                </div>
              </div>

              {accommodation.booking_with_us && (
                <div className="border-t pt-5">
                  <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-3">
                    <div className="flex h-full flex-col gap-2">
                      <Label className="min-h-[3rem] text-sm font-medium leading-tight flex items-end">HCTE Night Rate</Label>
                      <MoneyInput
                        value={accommodation.hcte_night_rate ?? accommodation.booking_price}
                        onChange={(value) => {
                          updateAccommodation(index, "hcte_night_rate", parseMoneyValue(value));
                        }}
                        placeholder="Enter HCTE night rate"
                        className="w-full"
                        disabled={isReadOnly || accommodation.free_of_charge}
                      />
                      <div className="text-xs text-muted-foreground">
                        Client sees:{" "}
                        <PriceWithAdjustment
                          baseAmount={
                            accommodation.free_of_charge
                              ? 0
                              : Number(accommodation.hcte_night_rate ?? accommodation.booking_price ?? 0)
                          }
                          proposalData={data}
                          emptyLabel={accommodation.free_of_charge ? "Free" : undefined}
                        />
                        <span className="ml-1">per night</span>
                      </div>
                    </div>
                    <div className="flex h-full flex-col gap-2">
                      <Label className="min-h-[3rem] text-sm font-medium leading-tight flex items-end">
                        Accommodation Website Night Rate
                      </Label>
                      <MoneyInput
                        value={accommodation.accommodation_website_night_rate}
                        onChange={(value) => {
                          updateAccommodation(index, "accommodation_website_night_rate", parseMoneyValue(value));
                        }}
                        placeholder="Enter website night rate"
                        className="w-full"
                        disabled={isReadOnly || accommodation.free_of_charge}
                      />
                      <div className="text-xs text-muted-foreground">
                        Client sees:{" "}
                        <PriceWithAdjustment
                          baseAmount={
                            accommodation.free_of_charge
                              ? 0
                              : Number(accommodation.accommodation_website_night_rate ?? 0)
                          }
                          proposalData={data}
                          emptyLabel={accommodation.free_of_charge ? "Free" : undefined}
                        />
                        <span className="ml-1">per night</span>
                      </div>
                    </div>
                    <div className="flex h-full flex-col gap-2">
                      <Label className="min-h-[3rem] text-sm font-medium leading-tight flex items-end">Booking.com Night Rate</Label>
                      <MoneyInput
                        value={accommodation.booking_com_night_rate}
                        onChange={(value) => {
                          updateAccommodation(index, "booking_com_night_rate", parseMoneyValue(value));
                        }}
                        placeholder="Enter Booking.com night rate"
                        className="w-full"
                        disabled={isReadOnly || accommodation.free_of_charge}
                      />
                      <div className="text-xs text-muted-foreground">
                        Client sees:{" "}
                        <PriceWithAdjustment
                          baseAmount={
                            accommodation.free_of_charge
                              ? 0
                              : Number(accommodation.booking_com_night_rate ?? 0)
                          }
                          proposalData={data}
                          emptyLabel={accommodation.free_of_charge ? "Free" : undefined}
                        />
                        <span className="ml-1">per night</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t pt-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Cancellation Policy</Label>
                  <Textarea
                    value={accommodation.cancellation_policy || ""}
                    onChange={(e) => updateAccommodation(index, "cancellation_policy", e.target.value)}
                    placeholder="Enter cancellation policy details..."
                    className="min-h-[100px]"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="flex justify-end border-t pt-3">
                <div className="text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Item total</p>
                  <PriceWithAdjustment
                    baseAmount={computeAccommodationBase(accommodation)}
                    proposalData={data}
                    emptyLabel={
                      accommodation.free_of_charge
                        ? "Free"
                        : accommodation.to_be_quoted
                          ? "To be quoted"
                          : !accommodation.booking_with_us
                            ? "Booked directly"
                            : undefined
                    }
                    size="md"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccommodationStep;
